import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Zap, 
  Shield, 
  Sliders, 
  HelpCircle, 
  Copy, 
  Check, 
  AlertTriangle, 
  ArrowRight,
  TrendingUp,
  BarChart2,
  Target,
  RefreshCw,
  MousePointer,
  Monitor
} from 'lucide-react';

const KsqProTrader = () => {
  const [billingCycle, setBillingCycle] = useState('lifetime');
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState('idle');
  const [generatedLicense, setGeneratedLicense] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  
  const paypalContainerRef = useRef(null);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  // Load PayPal SDK
  useEffect(() => {
    const loadPayPal = async () => {
      if (window.paypal) { setPaypalLoaded(true); return; }
      // Use the live client ID from environment variables, fallback to sandbox
      const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'sb';
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
      script.async = true;
      script.onload = () => setPaypalLoaded(true);
      document.body.appendChild(script);
    };
    loadPayPal();
  }, []);

  // Initialize PayPal Buttons
  useEffect(() => {
    if (paypalLoaded && checkoutStatus === 'idle' && window.paypal && paypalContainerRef.current) {
      paypalContainerRef.current.innerHTML = '';
      window.paypal.Buttons({
        style: { layout: 'vertical', color: 'gold', shape: 'pill', label: 'pay' },
        createOrder: (data, actions) => {
          const price = billingCycle === 'lifetime' ? '100.00' : '30.00';
          return actions.order.create({
            purchase_units: [{
              description: `KSQ ProTrader Chart Suite - ${billingCycle === 'lifetime' ? 'Lifetime' : 'Annual'} License`,
              amount: { currency_code: 'USD', value: price }
            }]
          });
        },
        onApprove: async (data, actions) => {
          setCheckoutStatus('paying');
          try {
            const details = await actions.order.capture();
            const payerEmail = details.payer.email_address;
            const payerName = `${details.payer.name.given_name || ''} ${details.payer.name.surname || ''}`.trim();
            const transactionId = details.id;
            const response = await fetch('https://us-central1-ksquants.cloudfunctions.net/createLicense', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: payerEmail, name: payerName, transactionId, secret: 'KSQ_SECURE_TOKEN_2026' })
            });
            const result = await response.json();
            if (result.status === 'success') {
              setGeneratedLicense(result.licenseKey);
              setCheckoutStatus('success');
            } else {
              setCheckoutStatus('error');
            }
          } catch (err) {
            setCheckoutStatus('error');
          }
        },
        onError: () => setCheckoutStatus('error')
      }).render(paypalContainerRef.current);
    }
  }, [paypalLoaded, checkoutStatus, billingCycle]);

  const copyLicense = () => {
    navigator.clipboard.writeText(generatedLicense);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const faqs = [
    {
      q: "How does it put TradingView inside MT5?",
      a: "Our proprietary smart-overlay embeds the full TradingView charting interface directly onto your MT5 workspace. A high-speed background bridge captures every action from your trading panel — Buy/Sell, SL, TP, Lot Size, and Close — and routes them natively to your broker's MT5 engine in real time. Zero browser switching required."
    },
    {
      q: "What does the Ultimate AI Copilot do?",
      a: "Our proprietary AI Copilot is an institutional-grade virtual analyst that sits right inside your terminal. It actively 'reads' your live chart visually, cross-references macroeconomic news events, evaluates the market structure, and provides a real-time Confidence Score for potential setups. You can even chat with it to ask follow-up questions about the current trend!"
    },
    {
      q: "Can I set Stop Loss, Take Profit, and Lot Size from within the overlay?",
      a: "Yes — completely. Our built-in pip calculator lets you enter your SL and TP in pips. It automatically computes the exact broker price levels and sends them directly to MT5. Lot sizing is also controlled from the same panel, so your entire position setup happens in one place before you click Buy or Sell."
    },
    {
      q: "Does this require special broker configuration?",
      a: "No. KSQ ProTrader Chart Suite works with any MT5 broker and automatically synchronizes to their specific market symbols, including broker-specific suffixes (e.g. converting TradingView's 'EURUSD' to your broker's 'EURUSD+'). You only need to whitelist one URL in MT5 to support the secure license check — we walk you through it in 60 seconds."
    },
    {
      q: "Can I use the license on multiple MT5 account numbers?",
      a: "Each license key is single-user. Upon the very first activation in MT5, it permanently binds to that active account number. For multiple accounts, contact support for a multi-account bundle."
    },
    {
      q: "Is there any latency in executing trades?",
      a: "Our bridge processes updates 10 times per second — execution speeds feel identical to clicking native MT5 buttons. Your orders go through your broker's normal execution path, so there is no added slippage or delay."
    },
    {
      q: "What if I experience issues with installation?",
      a: "The suite ships with an automated 1-click installer that configures your MT5 terminal and places all components in the correct folders automatically. Our support team is available 24/7 via WhatsApp and email if you ever need manual assistance."
    },
    {
      q: "Can I run KSQ ProTrader Chart Suite on a MacBook?",
      a: "The suite runs natively on Windows 10 and 11. MacBook users can run it seamlessly via a Windows VPS controlled via Remote Desktop — which is actually the recommended setup for 24/7 uptime — or by running Windows on Mac via Parallels Desktop."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden font-sans pt-20">

      {/* Background Gradient */}
      <div className="absolute top-0 left-0 w-full h-[700px] bg-gradient-to-b from-primary-950/25 via-slate-950 to-transparent pointer-events-none z-0" />

      {/* ── HERO SECTION ── */}
      <section className="relative min-h-[92vh] flex items-center justify-center py-12 px-6 z-10">
        <div className="max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-12 items-center">

          {/* Left: Value Copy */}
          <div className="lg:col-span-7 space-y-8 text-left">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-950/40 border border-primary-500/30 backdrop-blur-md">
              <span className="w-2 h-2 bg-primary-400 rounded-full animate-pulse" />
              <span className="text-primary-300 text-xs font-semibold tracking-wider uppercase font-orbitron">KSQ Trading Terminal</span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
              <span className="bg-gradient-to-r from-cyan-400 via-primary-400 to-pink-400 bg-clip-text text-transparent">
                KSQ ProTrader Chart Suite
              </span>
              <span className="block text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-3 font-semibold text-slate-100">
                One Platform. Analyze. Execute.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 font-light leading-relaxed max-w-2xl">
              Why switch between TradingView and MT5? <strong className="text-white">KSQ ProTrader Chart Suite ends that.</strong> Get full TradingView charting power embedded directly inside your MetaTrader 5 terminal. Execute trades with one click, and utilize our proprietary <strong className="text-primary-300">Ultimate AI Copilot</strong> to perform visual, institutional-grade market analysis in real-time.
            </p>

            {/* Feature Pills */}
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: TrendingUp, text: "Full TradingView Charts Inside MT5" },
                { icon: Target,     text: "Ultimate AI Copilot & Vision" },
                { icon: Zap,        text: "Open & Close Trades Instantly" },
                { icon: RefreshCw,  text: "Universal Broker Symbol Auto-Sync" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-slate-900/40 border border-slate-800 rounded-xl p-4 backdrop-blur-sm hover:border-primary-500/40 transition-colors duration-300">
                  <div className="p-2 rounded-lg bg-primary-950/50 text-primary-400 border border-primary-500/20 flex-shrink-0">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <span className="text-slate-200 text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 pt-2">
              <button
                onClick={() => scrollToSection('checkout')}
                className="group flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white font-bold rounded-xl shadow-lg shadow-primary-500/25 transition-all duration-300 transform hover:scale-[1.02]"
              >
                <span>Get ProTrader Chart Suite Now</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToSection('features')}
                className="px-8 py-4 bg-slate-900/60 hover:bg-slate-800/60 text-slate-200 font-semibold rounded-xl border border-slate-800 hover:border-slate-700 transition-all duration-300"
              >
                See All Features
              </button>
            </div>
          </div>

          {/* Right: Terminal Mockup */}
          <div className="lg:col-span-5 relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-pink-500/10 to-transparent blur-3xl opacity-30" />
            <div className="relative border border-slate-800 rounded-3xl bg-slate-900/50 backdrop-blur-xl p-3 shadow-2xl overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-500 via-primary-500 to-pink-500" />
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-800 text-slate-400 text-xs">
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500/80" />
                </div>
                <span className="font-mono text-slate-400 uppercase tracking-wider font-semibold">KSQ_ProTrader_Terminal</span>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-green-400 font-semibold font-mono">ACTIVE</span>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden mt-2 border border-slate-800 bg-slate-950">
                <img
                  src="/protrader_terminal.png"
                  alt="KSQ ProTrader Terminal — TradingView embedded inside MT5"
                  className="w-full h-auto object-cover transform hover:scale-[1.01] transition-transform duration-500"
                />
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── PROBLEM → SOLUTION SECTION ── */}
      <section className="py-20 border-t border-slate-900 bg-slate-950/60 relative z-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              The Old Way vs. The KSQ Way
            </h2>
            <p className="text-slate-400 text-lg">Two platforms, two problems — one solution.</p>
          </div>

          <div className="space-y-6">
            {/* Step 01 */}
            <div className="grid md:grid-cols-2 gap-4 items-stretch">
              <div className="relative bg-slate-900/40 border border-red-900/30 rounded-2xl p-6">
                <span className="text-red-500/30 font-black text-6xl absolute top-3 right-4 font-orbitron leading-none select-none">01</span>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-red-950/50 text-red-400 border border-red-900/30">
                    <BarChart2 className="w-5 h-5" />
                  </div>
                  <span className="text-red-400 text-xs font-bold uppercase tracking-widest">Before</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">You open TradingView separately to analyse the market — drawing levels, applying indicators, reading multi-timeframe setups across a second screen or second window.</p>
              </div>
              <div className="relative bg-primary-950/20 border border-primary-500/20 rounded-2xl p-6">
                <span className="text-primary-500/20 font-black text-6xl absolute top-3 right-4 font-orbitron leading-none select-none">01</span>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-primary-950/50 text-primary-400 border border-primary-500/20">
                    <Check className="w-5 h-5" />
                  </div>
                  <span className="text-primary-400 text-xs font-bold uppercase tracking-widest">With KSQ ProTrader Terminal</span>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">TradingView is <strong className="text-white">embedded directly inside MT5.</strong> All chart types, 100+ indicators, drawings, and multi-timeframe analysis — in one workspace, no window switching ever.</p>
              </div>
            </div>

            {/* Step 02 */}
            <div className="grid md:grid-cols-2 gap-4 items-stretch">
              <div className="relative bg-slate-900/40 border border-red-900/30 rounded-2xl p-6">
                <span className="text-red-500/30 font-black text-6xl absolute top-3 right-4 font-orbitron leading-none select-none">02</span>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-red-950/50 text-red-400 border border-red-900/30">
                    <Monitor className="w-5 h-5" />
                  </div>
                  <span className="text-red-400 text-xs font-bold uppercase tracking-widest">Before</span>
                </div>
                <p className="text-slate-400 text-sm leading-relaxed">You alt-tab to MT5, manually calculate SL/TP price levels, type in your lot size, open the order window, and click Buy or Sell — all under time pressure while the market moves.</p>
              </div>
              <div className="relative bg-primary-950/20 border border-primary-500/20 rounded-2xl p-6">
                <span className="text-primary-500/20 font-black text-6xl absolute top-3 right-4 font-orbitron leading-none select-none">02</span>
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-primary-950/50 text-primary-400 border border-primary-500/20">
                    <Check className="w-5 h-5" />
                  </div>
                  <span className="text-primary-400 text-xs font-bold uppercase tracking-widest">With KSQ ProTrader Terminal</span>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed">Enter your SL and TP in pips — our pip calculator <strong className="text-white">auto-converts to exact broker prices.</strong> Set your lot size. Click Buy or Sell. Your order fires to MT5 in under 100ms. Done.</p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex items-center justify-center gap-4">
            <div className="h-px flex-1 bg-gradient-to-r from-transparent to-green-500/40" />
            <span className="text-green-400 font-bold text-sm uppercase tracking-widest px-4">✓ KSQ ProTrader Chart Suite fixes both</span>
            <div className="h-px flex-1 bg-gradient-to-l from-transparent to-green-500/40" />
          </div>
        </div>
      </section>

      {/* ── FULL FEATURES SECTION ── */}
      <section id="features" className="py-24 border-t border-slate-900 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-slate-100 to-slate-400 bg-clip-text text-transparent mb-6">
              One Workspace. Zero Friction.
            </h2>
            <p className="text-slate-400 text-lg">
              Every tool you need to analyse and execute — without leaving a single screen.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: BarChart2,
                title: "Full TradingView Charting",
                desc: "Access the complete TradingView interface — all chart types, 100+ indicators, drawing tools, and multi-timeframe analysis — embedded live inside your MT5 workspace.",
              },
              {
                icon: Shield,
                title: "Ultimate AI Copilot",
                desc: "Your personal, institutional-grade AI analyst. It actively 'sees' your live chart, analyzes market structure and economic news, and provides real-time setup confidence scoring.",
                highlight: true
              },
              {
                icon: Zap,
                title: "Buy & Sell with One Click",
                desc: "Open long or short positions directly from the TradingView panel. Our sub-100ms bridge routes your order straight to MT5 and your broker without any window switching.",
              },
              {
                icon: Target,
                title: "SL & TP Pip Calculator",
                desc: "Enter your Stop Loss and Take Profit in pips. The engine automatically converts them to exact broker price levels and pre-fills them on your MT5 order — no manual maths under pressure.",
              },
              {
                icon: Sliders,
                title: "Lot Size Control",
                desc: "Set your position size directly from the trading panel before you execute. Full control over your risk per trade without ever opening a separate MT5 order window.",
              },
              {
                icon: MousePointer,
                title: "One-Click Position Close",
                desc: "Close any open position instantly with a single button. No navigating through trade lists or order menus — your exit is as fast as your entry.",
              },
              {
                icon: RefreshCw,
                title: "Universal Broker Symbol Sync",
                desc: "Automatically synchronizes to your broker's exact symbol naming — including suffixes like '.m', '+' or '#'. Chart EURUSD on TradingView and it routes to your broker's exact instrument.",
              },
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08 }}
                className={`bg-gradient-to-br from-slate-900/60 to-slate-900/30 border rounded-2xl p-8 hover:bg-slate-900/80 transition-all duration-300 group ${feature.highlight ? 'border-primary-500/40 shadow-lg shadow-primary-500/10' : 'border-slate-800 hover:border-primary-500/30'}`}
              >
                <div className={`w-12 h-12 rounded-xl border flex items-center justify-center mb-6 group-hover:scale-105 transition-transform ${feature.highlight ? 'bg-primary-600/20 border-primary-500/40 text-primary-300' : 'bg-primary-950/50 border-primary-500/20 text-primary-400'}`}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-slate-100 mb-3 group-hover:text-primary-300 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-slate-400 leading-relaxed text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-24 border-t border-slate-900 bg-slate-950/50 relative z-10">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-slate-400 text-lg">Up and running in minutes. Trading smarter immediately.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { num: "1", title: "Buy & Install", desc: "Get your license key instantly after payment. Run the 1-click installer and it sets up everything in MT5 automatically." },
              { num: "2", title: "Open MT5", desc: "Drag the KSQ ProTrader Chart Suite EA onto any chart. The TradingView window embeds directly into your workspace." },
              { num: "3", title: "Analyse", desc: "Use the full power of TradingView — indicators, drawings, multi-timeframe — without leaving MT5." },
              { num: "4", title: "Execute", desc: "Set your lot size, SL and TP, then click Buy or Sell. Your trade fires through MT5 to your broker in milliseconds." },
            ].map((step, idx) => (
              <div key={idx} className="text-center relative">
                {idx < 3 && (
                  <div className="hidden md:block absolute top-8 left-[55%] w-full h-px border-t border-dashed border-slate-700 z-0" />
                )}
                <div className="relative z-10 w-16 h-16 rounded-full bg-gradient-to-br from-primary-600 to-primary-800 border border-primary-500/40 text-white font-black text-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary-500/20">
                  {step.num}
                </div>
                <h4 className="font-bold text-slate-100 mb-2">{step.title}</h4>
                <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING / CHECKOUT ── */}
      <section id="checkout" className="py-24 border-t border-slate-900 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-slate-100 to-slate-400 bg-clip-text text-transparent mb-6">
              Get Your License Key Instantly
            </h2>
            <p className="text-slate-400 text-lg">
              Fully automated. Your key is generated and emailed the moment payment clears — even at 3am.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-12 gap-8 items-stretch">

              {/* Left: Plan Details */}
              <div className="md:col-span-7 bg-slate-900/40 border border-slate-800 rounded-3xl p-8 flex flex-col justify-between backdrop-blur-md">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-100">KSQ ProTrader Chart Suite</h3>
                    <p className="text-slate-400 text-sm mt-1">Full TradingView + MT5 unified terminal</p>
                  </div>

                  {/* Billing Toggle */}
                  <div className="inline-flex bg-slate-950 rounded-xl p-1 border border-slate-800">
                    <button
                      onClick={() => setBillingCycle('annual')}
                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${billingCycle === 'annual' ? 'bg-primary-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                    >
                      Annual Access
                    </button>
                    <button
                      onClick={() => setBillingCycle('lifetime')}
                      className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all relative ${billingCycle === 'lifetime' ? 'bg-primary-600 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                    >
                      Lifetime License
                      <span className="absolute -top-2 -right-3 bg-green-500 text-white text-[9px] px-1.5 py-0.5 rounded-full font-bold">BEST</span>
                    </button>
                  </div>

                  {/* Feature Checklist */}
                  <ul className="space-y-3 pt-2">
                    {[
                      "Full TradingView charting embedded in MT5",
                      "Open & close Buy/Sell positions with one click",
                      "SL, TP & Lot Size set from one panel",
                      "Pip calculator — auto-converts to broker prices",
                      "Universal broker symbol auto-synchronization",
                      "License key delivered instantly after payment",
                      "1-Click Windows installer — no technical setup",
                      billingCycle === 'lifetime' ? "All future v2+ updates included — forever" : "Access valid for 12 months from purchase date",
                      "30-Day unconditional money-back guarantee",
                    ].map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Compatibility Note */}
                  <div className="bg-slate-950/60 border border-slate-800/85 rounded-xl p-3.5 mt-4 text-[13px] text-slate-400 flex items-start gap-2.5">
                    <span className="text-primary-400 font-semibold uppercase tracking-wider block text-[9px] bg-primary-950/50 px-2 py-0.5 rounded border border-primary-500/20 mt-0.5 flex-shrink-0">COMPAT</span>
                    <div>
                      <span className="font-semibold text-slate-300">Windows 10/11 &amp; Windows VPS</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">MacBook users: run seamlessly via Windows VPS (Remote Desktop) or Parallels Desktop.</span>
                    </div>
                  </div>
                </div>

                {/* Price Footer */}
                <div className="mt-8 pt-6 border-t border-slate-800/60 flex items-center justify-between bg-slate-950/40 p-4 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <Shield className="w-10 h-10 text-primary-400" />
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">30-Day Guarantee</h4>
                      <p className="text-[11px] text-slate-500">Full refund, no questions asked</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-400">
                      ${billingCycle === 'lifetime' ? '100' : '30'} USD
                    </div>
                    <p className="text-[10px] text-slate-500">{billingCycle === 'lifetime' ? 'one-time payment' : 'billed annually'}</p>
                  </div>
                </div>
              </div>

              {/* Right: Payment */}
              <div className="md:col-span-5 bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 rounded-3xl p-8 flex flex-col justify-center relative overflow-hidden">

                {/* IDLE */}
                <div className={checkoutStatus === 'idle' ? 'space-y-6 block w-full' : 'hidden'}>
                  <h4 className="text-lg font-bold text-slate-100 text-center">Complete Purchase</h4>
                  <p className="text-slate-400 text-xs text-center leading-relaxed">
                    Pay securely via PayPal or credit card. License generated instantly.
                  </p>
                  {paypalLoaded ? (
                    <div id="paypal-button-container" ref={paypalContainerRef} className="w-full min-h-[150px]" />
                  ) : (
                    <div className="flex flex-col items-center justify-center py-8 space-y-3">
                      <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
                      <span className="text-xs text-slate-400 font-medium">Loading secure payment...</span>
                    </div>
                  )}
                </div>

                {/* PAYING */}
                {checkoutStatus === 'paying' && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <div className="animate-spin w-12 h-12 border-2 border-green-500 border-t-transparent rounded-full" />
                    <h4 className="text-lg font-bold text-white">Processing Payment...</h4>
                    <p className="text-slate-400 text-xs text-center px-4">Keep this window open. Validating payment and generating your license key.</p>
                  </div>
                )}

                {/* SUCCESS */}
                {checkoutStatus === 'success' && (
                  <div className="space-y-6 text-center animate-fade-in">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 border border-green-500 text-green-400 flex items-center justify-center mx-auto text-3xl">✓</div>
                    <div>
                      <h4 className="text-xl font-bold text-white">License Key Ready!</h4>
                      <p className="text-green-400 text-xs mt-1">Setup guide sent to your email.</p>
                    </div>
                    <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 relative group">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Your License Key</span>
                      <code className="text-primary-300 font-mono font-bold text-sm block tracking-widest break-all select-all">{generatedLicense}</code>
                      <button onClick={copyLicense} className="absolute right-2 top-2 p-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-400 hover:text-white transition-colors">
                        {copySuccess ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <div className="space-y-3 pt-2">
                      <a href="/downloads/KSQ_ProTraderSuite_v2.zip" className="flex items-center justify-center gap-2 w-full py-3.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold rounded-xl shadow-lg transition-all">
                        <span>Download Software Suite</span>
                      </a>
                      <p className="text-[10px] text-slate-500">Extract the ZIP and run <code>Install_KSQ_ProTrader.bat</code> as Administrator.</p>
                    </div>
                  </div>
                )}

                {/* ERROR */}
                {checkoutStatus === 'error' && (
                  <div className="space-y-6 text-center animate-fade-in">
                    <div className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500 text-red-400 flex items-center justify-center mx-auto">
                      <AlertTriangle className="w-8 h-8" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white">Payment Error</h4>
                      <p className="text-slate-400 text-xs mt-1">We couldn't process your transaction. Please try again.</p>
                    </div>
                    <button onClick={() => setCheckoutStatus('idle')} className="w-full py-3 bg-slate-900 border border-slate-800 text-white font-semibold rounded-xl hover:bg-slate-800 transition-all text-sm">
                      Try Again
                    </button>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-24 border-t border-slate-900 bg-slate-950/20 relative z-10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">Frequently Asked Questions</h2>
            <p className="text-slate-400 text-lg">Everything you need to know about KSQ ProTrader Chart Suite before you start trading smarter.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border border-slate-800 rounded-2xl bg-slate-900/25 overflow-hidden transition-all duration-300">
                <button
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="w-full flex justify-between items-center p-6 text-left hover:bg-slate-900/40 transition-colors"
                >
                  <span className="font-semibold text-slate-100 text-base md:text-lg pr-4">{faq.q}</span>
                  <HelpCircle className={`w-5 h-5 text-primary-400 transition-transform flex-shrink-0 ${activeFaq === idx ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence initial={false}>
                  {activeFaq === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="px-6 pb-6 pt-2 border-t border-slate-800/40 text-slate-400 text-sm leading-relaxed">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Final CTA */}
          <div className="mt-16 text-center bg-gradient-to-br from-primary-950/40 to-slate-900/60 border border-primary-500/20 rounded-3xl p-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">Ready to Trade Smarter?</h3>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">Stop splitting your workflow between platforms. KSQ ProTrader Chart Suite puts everything in one place — chart, calculate, and execute with one click.</p>
            <button
              onClick={() => scrollToSection('checkout')}
              className="group inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white font-bold rounded-xl shadow-lg shadow-primary-500/25 transition-all duration-300 transform hover:scale-[1.02]"
            >
              <span>Get ProTrader Chart Suite Now — $100 Lifetime</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <p className="text-slate-500 text-xs mt-4">30-Day money-back guarantee · Instant delivery · No subscriptions</p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default KsqProTrader;
